import type { DeployableId, GrenadeId, PackId, WeaponId } from './game';

export const MOVEMENT = {
  tickRate: 1 / 60,
  gravity: 20.0,
  groundAcceleration: 50,
  groundLowSpeedResponse: 18,
  groundLowSpeedAccelerationBoost: 0.34,
  airAcceleration: 15,
  skiDrag: 0.025,
  skiDragAtSpeed: 0.04,
  skiRunawayDrag: 0.055,
  skiSurfaceSnap: 1.55,
  skiSlopeSampleDistance: 2,
  skiSlopeAssistScale: 6.2,
  skiCarrySpeed: 78,
  skiCarryMaxBoost: 0.42,
  skiDownhillAssist: 0.54,
  skiDownhillTransferCap: 19,
  skiUphillPenalty: 0.14,
  skiSeamForgiveness: 1.35,
  skiSeamSpeedScale: 64,
  skiLandingCarry: 0.28,
  skiLandingCarrySpeed: 34,
  skiLandingCarryMultiplier: 0.52,
  skiGroundAccelerationMultiplier: 0.82,
  skiCarveSpeedThreshold: 34,
  skiCarveAccelerationFloor: 0.08,
  skiCarveAlignedAccelerationBonus: 0.18,
  skiCarveHardTurnDrag: 0.34,
  skiBoostImpulse: 58,
  skiBoostEnergyDrainPerSecond: 42,
  skiBoostMinEnergy: 8,
  groundDrag: 2.5,
  airDrag: 0.17,
  jetImpulse: 20,
  jetEmergencyLiftImpulse: 48,
  jetEmergencyLiftThreshold: 10,
  jetEmergencyLiftWindow: 16,
  jetLiftBase: 1.1,
  jetLiftFalloffPerVerticalSpeed: 0.012,
  jetLiftMinMultiplier: 0.94,
  jetLiftMaxMultiplier: 1.24,
  jetForwardImpulse: 7.4,
  jetForwardPitchWindow: 0.85,
  jetForwardMoveThreshold: 0.2,
  jetPitchVectorWindow: 0.7,
  jetPitchVectorHorizontalTransfer: 0.8,
  jetPitchVectorLiftTradeoff: 0.64,
  jumpJetBlend: 0.8,
  jumpImpulse: 68,
  energyDrainPerSecond: 30,
  energyRegenPerSecond: 25,
  energyAirborneRegenPerSecond: 8,
  maxEnergy: 100,
  jetIgnitionBurst: 1.5,
  jetIgnitionMinEnergy: 5,
  slopeSlideMinAngleDeg: 30,
  slopeSlideAcceleration: 6,
  skiCoyoteSeconds: 0.12,
  terminalFallSpeed: 80,
};

export const COMBAT = {
  healthRegenPerSecond: 6,
  respawnHeight: 18,
  fireIntervalTicks: 12,
  playerHitRadius: 2.4,
  selfDamageGraceSeconds: 0.2,
};

export const MATCH = {
  warmupSeconds: 5,
  liveSeconds: 180,
  postgameSeconds: 6,
  scoreToWin: 3,
};

export const OBJECTIVES = {
  flagPickupRadius: 7,
  flagReturnSeconds: 6,
  standoffThresholdSeconds: 12,
  standoffEscalationSeconds: 20,
  standoffDroppedReturnSeconds: 3.25,
  boostDurationSeconds: 2.25,
  boostPadCooldownSeconds: 7,
  boostMultiplier: 1.85,
  boostPadMinSpeed: 38,
  boostPadMaxSpeed: 78,
  spawnProtectionSeconds: 2.5,
};

export const CTF = {
  standoffEscalationSeconds: 6,
  standoffCompressionSeconds: 10,
  standoffCompressedReturnSeconds: 2.4,
} as const;

export const BASE_SYSTEMS = {
  generatorHealth: 180,
  generatorRespawnSeconds: 22,
  repairHealthPerSecond: 30,
  repairEnergyPerSecond: 34,
  repairHeatDecayPerSecond: 95,
  repairPulseIntervalTicks: 12,
  turretRange: 44,
  turretDamage: 8,
  turretImpulse: 3.2,
  turretCooldownSeconds: 0.18,
};

export const WEAPON = {
  defaultSlot: 1,
  heatDecayPerSecond: 18,
  overheatedThreshold: 100,
  overheatedCooldownSeconds: 1.2,
};

// Phase 72/73: chaingun spin-up before first shot + spread accumulating with sustained fire
export const CHAINGUN = {
  spinUpSeconds: 0.35,
  spreadHeatPerSecond: 0.12,
  spreadCooldownPerSecond: 0.45,
  maxSpreadRadians: 0.06,
};

// Phase 171-175: firing-time mechanics for the new T2 arsenal.
export const WEAPON_MECHANICS = {
  // Laser rifle: costs player energy per shot; damage scales with the energy
  // available at fire time, giving snipers a charge-management skill floor.
  laserRifle: {
    energyCost: 45,
    minChargeMultiplier: 0.55,
    maxChargeMultiplier: 1.4,
  },
  // ELF projector: drains the target's movement energy and feeds a little back
  // to the wielder each hit, grounding jetting light armors.
  elf: {
    energyDrainPerHit: 8,
    selfEnergyGainPerHit: 2.5,
  },
  // Shocklance: massive rear-hit bonus. rearDotThreshold is the dot product
  // between the target's facing and the attacker->target vector above which the
  // hit counts as "from behind".
  shocklance: {
    rearDamageMultiplier: 2.6,
    rearDotThreshold: 0.4,
  },
} as const;

export const GRENADE = {
  defaultId: 'frag' as GrenadeId,
  maxCharges: 2,
  throwCooldownSeconds: 1.15,
};

export const TACTICAL = {
  commandTtlSeconds: 14,
  commandCooldownSeconds: 6,
  pingTtlSeconds: 7,
  pingCooldownSeconds: 2.2,
  pingDistance: 42,
  maxPings: 24,
};

export const PACK = {
  defaultId: 'energy' as PackId,
  maxEnergy: 100,
  energyRegenPerSecond: 20,
  repairPulseIntervalTicks: 12,
};

export const PACKS: Record<
  PackId,
  {
    activationCost: number;
    activeDurationSeconds: number;
    cooldownSeconds: number;
    activeEnergyDrainPerSecond: number;
    passiveEnergyRegenPerSecond?: number;
    activeEnergyRegenPerSecond?: number;
    activationBurstEnergy?: number;
    healPerSecond?: number;
    damageReduction?: number;
  }
> = {
  energy: {
    activationCost: 24,
    activeDurationSeconds: 1.8,
    cooldownSeconds: 5.5,
    activeEnergyDrainPerSecond: 12,
    passiveEnergyRegenPerSecond: 6,
    activeEnergyRegenPerSecond: 26,
    activationBurstEnergy: 24,
  },
  repair: {
    activationCost: 28,
    activeDurationSeconds: 2.4,
    cooldownSeconds: 7,
    activeEnergyDrainPerSecond: 10,
    healPerSecond: 24,
  },
  shield: {
    // Phase 123: 40% damage absorption while active; drains from pack-energy meter.
    activationCost: 30,
    activeDurationSeconds: 2.6,
    cooldownSeconds: 8.5,
    activeEnergyDrainPerSecond: 14,
    damageReduction: 0.4,
  },
  sensor: {
    activationCost: 18,
    activeDurationSeconds: 3.0,
    cooldownSeconds: 8,
    activeEnergyDrainPerSecond: 8,
  },
  jammer: {
    // Phase 154 — area jammer drains faster than cloak (28>22 per second).
    activationCost: 22,
    activeDurationSeconds: 2.5,
    cooldownSeconds: 10,
    activeEnergyDrainPerSecond: 28,
  },
  // Phase 125: Ammo pack — passive +25% mag bonus, no active state.
  ammo: {
    activationCost: 0,
    activeDurationSeconds: 0,
    cooldownSeconds: 0,
    activeEnergyDrainPerSecond: 0,
  },
  // Phase 126: Cloak pack — short, expensive invisibility burst.
  cloak: {
    activationCost: 28,
    activeDurationSeconds: 2.2,
    cooldownSeconds: 9,
    activeEnergyDrainPerSecond: 22,
  },
};

// Phase 125: Ammo pack passive multiplier consumed by mag/grenade/ammo logic.
export const PACK_AMMO_MAG_MULTIPLIER = 1.25;

export type SplashCurve = 'linear' | 'quadratic';

export const WEAPONS: Record<
  WeaponId,
  {
    slot: number;
    combatRole: 'burst' | 'sustained' | 'fallback' | 'heavy';
    fireMode: 'projectile' | 'hitscan';
    cooldownSeconds: number;
    heatPerShot: number;
    projectileSpeed?: number;
    projectileLifetime?: number;
    projectileRadius?: number;
    projectileGravity?: number;
    splashRadius?: number;
    directDamage?: number;
    knockback?: number;
    armTimeSeconds?: number;
    hitscanRange?: number;
    hitscanDamage?: number;
    hitscanRadius?: number;
    selfDamageMultiplier?: number;
    selfKnockbackMultiplier?: number;
    splashCurve?: SplashCurve;
    vehicleDamageMultiplier?: number;
  }
> = {
  spinfusor: {
    // Phase 71: authentic T2 disc — ~85 u/s, slight gravity arc, long ~4s lifetime
    slot: 1,
    combatRole: 'burst',
    fireMode: 'projectile',
    cooldownSeconds: 0.82,
    heatPerShot: 8,
    projectileSpeed: 85,
    projectileLifetime: 4.0,
    projectileRadius: 2.6,
    projectileGravity: MOVEMENT.gravity * 0.18,
    splashRadius: 17,
    directDamage: 88,
    knockback: 26,
    armTimeSeconds: 0.2,
    selfDamageMultiplier: 0.4,
    selfKnockbackMultiplier: 1.0,
    splashCurve: 'quadratic',
    vehicleDamageMultiplier: 1.5,
  },
  chaingun: {
    slot: 2,
    combatRole: 'sustained',
    fireMode: 'hitscan',
    cooldownSeconds: COMBAT.fireIntervalTicks / 60,
    heatPerShot: 9,
    hitscanRange: 110,
    hitscanDamage: 12,
    hitscanRadius: 3.2,
    selfDamageMultiplier: 0,
    selfKnockbackMultiplier: 0,
    vehicleDamageMultiplier: 0.8,
  },
  mortar: {
    // Phase 75: slow shell, big arc applied at fire time in server/state.ts
    slot: 3,
    combatRole: 'heavy',
    fireMode: 'projectile',
    cooldownSeconds: 1.7,
    heatPerShot: 12,
    projectileSpeed: 50,
    projectileLifetime: 3.4,
    projectileRadius: 3.4,
    projectileGravity: MOVEMENT.gravity,
    splashRadius: 24,
    directDamage: 135,
    knockback: 34,
    armTimeSeconds: 0.25,
    selfDamageMultiplier: 0.5,
    selfKnockbackMultiplier: 0.8,
    splashCurve: 'quadratic',
    vehicleDamageMultiplier: 2.0,
  },
  blaster: {
    slot: 4,
    combatRole: 'fallback',
    fireMode: 'projectile',
    cooldownSeconds: 0.22,
    heatPerShot: 10,
    projectileSpeed: 110,
    projectileLifetime: 1.2,
    projectileRadius: 1.2,
    projectileGravity: 0,
    splashRadius: 2.8,
    directDamage: 28,
    knockback: 5,
    armTimeSeconds: 0.05,
    selfDamageMultiplier: 0.1,
    selfKnockbackMultiplier: 0.3,
    splashCurve: 'linear',
    vehicleDamageMultiplier: 0.4,
  },
  'laser-rifle': {
    // Phase 171: sniper — long-range hitscan, high base damage, charge multiplier
    // applied at fire time in server/state.ts based on held energy.
    slot: 5,
    combatRole: 'burst',
    fireMode: 'hitscan',
    cooldownSeconds: 1.1,
    heatPerShot: 34,
    hitscanRange: 320,
    hitscanDamage: 80,
    hitscanRadius: 1.4,
    selfDamageMultiplier: 0,
    selfKnockbackMultiplier: 0,
    vehicleDamageMultiplier: 1.1,
  },
  plasma: {
    // Phase 172: arcing plasma bolt — strong vs armor, moderate splash, short lifetime
    slot: 6,
    combatRole: 'sustained',
    fireMode: 'projectile',
    cooldownSeconds: 0.42,
    heatPerShot: 14,
    projectileSpeed: 72,
    projectileLifetime: 1.6,
    projectileRadius: 2.0,
    projectileGravity: MOVEMENT.gravity * 0.32,
    splashRadius: 8,
    directDamage: 46,
    knockback: 10,
    armTimeSeconds: 0.08,
    selfDamageMultiplier: 0.25,
    selfKnockbackMultiplier: 0.5,
    splashCurve: 'linear',
    vehicleDamageMultiplier: 1.3,
  },
  'grenade-launcher': {
    // Phase 173: handheld bouncing grenade — arcs, bounces (like frag), medium splash
    slot: 7,
    combatRole: 'burst',
    fireMode: 'projectile',
    cooldownSeconds: 0.9,
    heatPerShot: 12,
    projectileSpeed: 52,
    projectileLifetime: 2.4,
    projectileRadius: 2.4,
    projectileGravity: MOVEMENT.gravity * 0.78,
    splashRadius: 16,
    directDamage: 66,
    knockback: 22,
    armTimeSeconds: 0.18,
    selfDamageMultiplier: 0.45,
    selfKnockbackMultiplier: 0.9,
    splashCurve: 'linear',
    vehicleDamageMultiplier: 1.2,
  },
  elf: {
    // Phase 174: continuous energy-drain beam — very low damage, drains target energy
    // (drain applied at fire time in server/state.ts). Short range, no heat build.
    slot: 8,
    combatRole: 'sustained',
    fireMode: 'hitscan',
    cooldownSeconds: 0.1,
    heatPerShot: 2,
    hitscanRange: 42,
    hitscanDamage: 3,
    hitscanRadius: 2.0,
    selfDamageMultiplier: 0,
    selfKnockbackMultiplier: 0,
    vehicleDamageMultiplier: 0.5,
  },
  shocklance: {
    // Phase 175: close-range instant-hit lance — huge burst, rear-hit bonus applied
    // at fire time. Very short range forces point-blank engagements.
    slot: 9,
    combatRole: 'burst',
    fireMode: 'hitscan',
    cooldownSeconds: 1.2,
    heatPerShot: 28,
    hitscanRange: 14,
    hitscanDamage: 70,
    hitscanRadius: 2.2,
    selfDamageMultiplier: 0,
    selfKnockbackMultiplier: 0,
    vehicleDamageMultiplier: 0.8,
  },
  'missile-launcher': {
    // Phase 176: handheld heavy dumb-fire missile — vehicle killer, big splash
    slot: 10,
    combatRole: 'heavy',
    fireMode: 'projectile',
    cooldownSeconds: 2.0,
    heatPerShot: 24,
    projectileSpeed: 66,
    projectileLifetime: 3.2,
    projectileRadius: 1.6,
    projectileGravity: MOVEMENT.gravity * 0.05,
    splashRadius: 14,
    directDamage: 90,
    knockback: 30,
    armTimeSeconds: 0.15,
    selfDamageMultiplier: 0.35,
    selfKnockbackMultiplier: 0.6,
    splashCurve: 'quadratic',
    vehicleDamageMultiplier: 2.4,
  },
  frag: {
    // Phase 74: ~3s fuse so the grenade bounces a few times before exploding
    slot: 0,
    combatRole: 'burst',
    fireMode: 'projectile',
    cooldownSeconds: GRENADE.throwCooldownSeconds,
    heatPerShot: 0,
    projectileSpeed: 44,
    projectileLifetime: 3.0,
    projectileRadius: 2.4,
    projectileGravity: MOVEMENT.gravity * 0.74,
    splashRadius: 18,
    directDamage: 82,
    knockback: 26,
    armTimeSeconds: 0.2,
    splashCurve: 'linear',
  },
  concussion: {
    slot: 0,
    combatRole: 'burst',
    fireMode: 'projectile',
    cooldownSeconds: GRENADE.throwCooldownSeconds,
    heatPerShot: 0,
    projectileSpeed: 40,
    projectileLifetime: 1.55,
    projectileRadius: 2.2,
    projectileGravity: MOVEMENT.gravity * 0.52,
    splashRadius: 19,
    directDamage: 18,
    knockback: 62,
    armTimeSeconds: 0.16,
    splashCurve: 'linear',
  },
  'vehicle-blaster': {
    slot: 0,
    combatRole: 'sustained',
    fireMode: 'projectile',
    cooldownSeconds: 0.2,
    heatPerShot: 0,
    projectileSpeed: 180,
    projectileLifetime: 1.5,
    projectileRadius: 1.0,
    projectileGravity: 0,
    splashRadius: 3,
    directDamage: 35,
    knockback: 8,
    armTimeSeconds: 0.05,
    selfDamageMultiplier: 0.1,
    selfKnockbackMultiplier: 0.3,
    splashCurve: 'linear',
    vehicleDamageMultiplier: 1.0,
  },
  'vehicle-missile': {
    slot: 0,
    combatRole: 'heavy',
    fireMode: 'projectile',
    cooldownSeconds: 2.5,
    heatPerShot: 0,
    projectileSpeed: 70,
    projectileLifetime: 3.0,
    projectileRadius: 1.5,
    projectileGravity: 0,
    splashRadius: 10,
    directDamage: 95,
    knockback: 28,
    armTimeSeconds: 0.15,
    selfDamageMultiplier: 0.3,
    selfKnockbackMultiplier: 0.5,
    splashCurve: 'linear',
    vehicleDamageMultiplier: 1.5,
  },
};

export const BASE_RADAR_RANGE = 80;

// Phase 151 — base sensor pulse coverage radius. Enemies within ~150u of any
// friendly *base* sensor become visible on radar (in addition to the existing
// proximity-radar and deployable sensor-beacon reveal paths).
export const SENSOR_COVERAGE_RADIUS = 150;
// Phase 154 — jammer pack masks the carrier AND any friendlies within this
// radius from enemy radar. Drains faster than cloak (configured in PACKS).
export const JAMMER_TEAM_MASK_RADIUS = 20;

export const DEPLOYABLE: Record<
  DeployableId,
  {
    radius: number;
    ttlSeconds: number;
    health: number;
    maxPerTeam: number;
    damage?: number;
    cooldownSeconds?: number;
    radarRange?: number;
    shieldRange?: number;
    damageReduction?: number;
    fireRange?: number;
    // Phase 179: anti-air / anti-vehicle turret fields.
    targetsVehicles?: boolean;
    vehicleDamage?: number;
    prioritizesAir?: boolean;
    ignoresElevationBlock?: boolean;
    leadsTargets?: boolean;
  }
> = {
  'sensor-beacon': { radius: 20, ttlSeconds: 90, health: 40, maxPerTeam: 4, radarRange: 80 },
  'shield-node': { radius: 8, ttlSeconds: 60, health: 60, maxPerTeam: 3, shieldRange: 30, damageReduction: 0.6 },
  'turret-small': { radius: 12, ttlSeconds: 120, health: 80, maxPerTeam: 2, damage: 22, cooldownSeconds: 1.5, fireRange: 60, targetsVehicles: true, vehicleDamage: 14, leadsTargets: true },
  // Phase 179: AA turret — longer range, faster, hits air/vehicles hard, leads targets,
  // and can elevate to track Shrikes overhead. Lighter anti-personnel damage.
  'turret-aa': { radius: 12, ttlSeconds: 110, health: 70, maxPerTeam: 2, damage: 16, cooldownSeconds: 0.5, fireRange: 95, targetsVehicles: true, vehicleDamage: 34, prioritizesAir: true, ignoresElevationBlock: true, leadsTargets: true },
  'ammo-supply': { radius: 10, ttlSeconds: 90, health: 30, maxPerTeam: 3 },
  // Phase 127: Proximity mine — short trip radius (3u), 80 damage on trip, splash via detonation.
  mine: { radius: 3, ttlSeconds: 60, health: 1, maxPerTeam: 4, damage: 80 },
  // Phase 128: Satchel charge — manually detonated, big AOE (200 dmg, 6u splash).
  satchel: { radius: 6, ttlSeconds: 45, health: 1, maxPerTeam: 1, damage: 200 },
};

// Phase 127/128: tuning for mine + satchel client-server contracts.
export const MINE_ARM_SECONDS = 1.0;
export const MINE_TRIP_RADIUS = 3.0;
export const SATCHEL_SPLASH_RADIUS = 6.0;
export const SATCHEL_DAMAGE = 200;

export const BOT_AIM_VARIANCE_DEGREES = 3.0;

// Phase 171-177: bumped for the six-weapon T2 arsenal expansion (laser rifle, plasma,
// grenade launcher, ELF, shocklance, missile launcher) and their loadout rules.
export const BALANCE_VERSION = 3;

export const WEAPON_SHAKE = {
  spinfusor: { intensity: 0.18, duration: 0.08 },
  mortar: { intensity: 0.28, duration: 0.12 },
  chaingun: { intensity: 0.06, duration: 0.04 },
  blaster: { intensity: 0.04, duration: 0.03 },
  'vehicle-blaster': { intensity: 0.05, duration: 0.03 },
  'vehicle-missile': { intensity: 0.24, duration: 0.1 },
} as const;

export const NETWORK = {
  serverPort: 5175,
};
